// 左パネルのトリガーボタン → 押すと中央に同デザインの選択Modalを開く。
// （以前は吹き出しだったが、作成/削除/返信と同じ中央Modalに統一）
import { ReactNode, useState } from "react";
import I18n from "common/i18n";
import { usePlaySound } from "common/utils";
import { InScreenModal } from "./ScreenModal";
import { useBuilderSidebar } from "./BuilderSidebar";
import { BuilderModalPanel, BuilderModalAccent } from "./BuilderModalPanel";

const ACCENT_FROM_BG: Record<string, BuilderModalAccent> = {
  "bg-green-100": BuilderModalAccent.GREEN,
  "bg-blue-100": BuilderModalAccent.BLUE,
  "bg-brown-80": BuilderModalAccent.BROWN,
};

export const SidebarDropdown = ({
  icon,
  label,
  displayText,
  accentTextClass,
  accentBgClass,
  accentBorderClass,
  active,
  badge,
  modalTitle,
  modalIcon,
  showClose,
  children,
}: {
  icon: string;
  // トリガーの表示ラベル（i18n キー）
  label: string;
  // 指定時はラベルの代わりにこの生文字列を表示
  displayText?: string;
  accentTextClass: string;
  accentBgClass: string;
  accentBorderClass: string;
  active: boolean;
  badge?: number;
  // Modal ヘッダーのタイトル/アイコン（未指定なら trigger の label/icon）
  modalTitle?: string;
  modalIcon?: string;
  // クリア型（左下=クリアで閉じる導線が無い）など、ヘッダー右上に × を出す
  showClose?: boolean;
  // 旧吹き出し用（現在は無視。呼び出し互換のため型に残す）
  wide?: boolean;
  openDown?: boolean;
  nudgeUp?: boolean;
  children: (close: () => void) => ReactNode;
}) => {
  const play = usePlaySound();
  const { collapsed } = useBuilderSidebar();
  const [open, setOpen] = useState(false);
  const accent = ACCENT_FROM_BG[accentBgClass] ?? BuilderModalAccent.GREEN;

  const handleToggle = () => {
    play();
    setOpen((o) => !o);
  };
  const close = () => setOpen(false);

  return (
    <div className={`flex-col-el relative w-full ${collapsed ? "items-center" : ""}`}>
      {collapsed ? (
        <button
          type="button"
          onClick={handleToggle}
          className={`type-inherit-font flex-col-center relative w-10 h-10 rounded-xl border-solid border-2 cursor-pointer ${
            active ? `${accentBgClass} ${accentBorderClass}` : `bg-white ${accentBorderClass}`
          }`}
        >
          <span
            className={`material-symbols-outlined type-md ${active ? "text-white" : accentTextClass}`}
          >
            {icon}
          </span>
          {badge !== undefined && badge > 0 && (
            <span
              className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                active ? "bg-white" : accentBgClass
              }`}
            />
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          className={`type-inherit-font flex-row-el items-center justify-between w-full gap-1 px-3 py-2 rounded-xl border-solid border-2 cursor-pointer ${
            active ? `${accentBgClass} ${accentBorderClass}` : `bg-white ${accentBorderClass}`
          }`}
        >
          <div className="flex-row-el items-center gap-1 min-w-0">
            <span
              className={`material-symbols-outlined type-sm ${active ? "text-white" : accentTextClass}`}
            >
              {icon}
            </span>
            <p
              className={`type-text type-sm whitespace-nowrap truncate ${
                active ? "text-white" : accentTextClass
              }`}
            >
              {displayText ?? I18n.t(label)}
            </p>
            {badge !== undefined && badge > 0 && (
              <span
                className={`flex-row-center shrink-0 min-w-5 h-5 px-1 rounded-full ${
                  active ? "bg-white" : accentBgClass
                }`}
              >
                <p className={`type-text type-sm ${active ? accentTextClass : "text-white"}`}>
                  {badge}
                </p>
              </span>
            )}
          </div>
          <span
            className={`material-symbols-outlined type-sm shrink-0 ${
              active ? "text-white" : accentTextClass
            }`}
          >
            tune
          </span>
        </button>
      )}

      {open && (
        <InScreenModal open onBackdropClose={close}>
          <BuilderModalPanel
            accent={accent}
            icon={modalIcon ?? icon}
            title={I18n.t(modalTitle ?? label)}
            width={480}
            onClose={showClose ? close : undefined}
          >
            {children(close)}
          </BuilderModalPanel>
        </InScreenModal>
      )}
    </div>
  );
};
